import { useState } from "react";
import { motion } from "framer-motion";
import { Smartphone, Apple, Monitor, Tv, Download, MessageCircle, Globe, ArrowUpRight } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { Reveal, SectionHead } from "./Reveal";
import { SetupGuide } from "./SetupGuide";

const ICONS = { Smartphone, Apple, Monitor, Tv };

const Step = ({ s, i, onOpen }) => {
  const Icon = ICONS[s.icon];
  return (
    <Reveal delay={i * 0.08} y={24} className="h-full">
      <motion.button type="button" onClick={() => onOpen(s)} data-testid={`setup-link-${s.id}`}
        whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="group relative flex w-full text-left h-full min-h-[300px] md:min-h-[340px] flex-col rounded-[28px] border border-white/10 bg-[#0f0d45] p-7 overflow-hidden hover:border-[#8a8dff]/60 transition-colors">
        <img src={`/img/set-${s.id}.jpg`} alt={s.title} loading="lazy"
          className="absolute inset-0 h-full w-full object-cover scale-105 transition-transform duration-[900ms] ease-out group-hover:scale-110" />
        <span className="absolute inset-0 bg-gradient-to-t from-[#08072a] via-[#0d0b3d]/85 to-[#0d0b3d]/35" />
        <span className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#5b5fd6]/0 blur-2xl transition-colors duration-500 group-hover:bg-[#5b5fd6]/35" />
        <div className="relative flex items-center justify-between">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white/10 backdrop-blur ring-1 ring-white/15 text-white group-hover:bg-white group-hover:text-[#5b5fd6] transition-colors">
            <Icon size={24} strokeWidth={1.6} />
          </span>
          <span className="font-display text-xs tracking-[0.3em] text-white/80">0{i + 1}</span>
        </div>
        <p lang="en" className="relative mt-auto pt-16 text-xs uppercase tracking-[0.25em] text-[#c9c6f0]">{s.app}</p>
        <h3 className="relative mt-2 font-display text-xl font-semibold leading-snug">{s.title}</h3>
        <span className="relative pt-6 inline-flex items-center gap-2 text-sm font-semibold text-white">
          <span className="grid h-9 w-9 place-items-center rounded-full border border-white/25 bg-white/5 backdrop-blur group-hover:bg-white group-hover:text-[#0d0b3d] transition-colors">
            <Download size={15} />
          </span>
          Kurulum rehberi
        </span>
      </motion.button>
    </Reveal>
  );
};

export default function Setup() {
  const { wa, setup, webPanel: WEB_PANEL } = useSite();
  const [open, setOpen] = useState(null);
  return (
    <section id="kurulum" className="relative bg-[#0d0b3d] py-24 md:py-36 overflow-hidden" data-testid="setup-section">
      <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-px w-2/3 bg-gradient-to-r from-transparent via-[#8a8dff]/60 to-transparent" />
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <SectionHead kicker="04 · Kurulum" title={<>Cihazını seç,<br />uygulamayı kur.</>} testid="setup-title"
          sub="Uygulamayı indir, WhatsApp'tan gelen hesap bilgilerinle giriş yap. Hepsi birkaç dakika sürer." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {setup.map((s, i) => <Step key={s.id} s={s} i={i} onOpen={setOpen} />)}
        </div>
        <SetupGuide s={open} onClose={() => setOpen(null)} help={wa("Merhaba, Dolunay TV kurulumu için yardım istiyorum.")} />
        <Reveal className="mt-4 md:mt-5">
          <a href={WEB_PANEL} target="_blank" rel="noreferrer" data-testid="setup-web-login"
            className="group relative flex flex-col md:flex-row md:items-center justify-between gap-6 overflow-hidden rounded-[28px] border border-white/10 p-7 md:p-10 hover:border-[#8a8dff]/60 transition-colors">
            <img src="/img/set-web.jpg" alt="Web'den giriş" loading="lazy"
              className="absolute inset-0 h-full w-full object-cover scale-105 transition-transform duration-[900ms] ease-out group-hover:scale-110" />
            <span className="absolute inset-0 bg-gradient-to-r from-[#08072a] via-[#0d0b3d]/85 to-[#0d0b3d]/40" />
            <div className="relative flex items-center gap-5">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/10 backdrop-blur ring-1 ring-white/15 group-hover:bg-white group-hover:text-[#5b5fd6] transition-colors">
                <Globe size={24} strokeWidth={1.6} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#c9c6f0]">Uygulama gerekmez</p>
                <h3 className="mt-1 font-display text-xl md:text-2xl font-semibold">Web'den giriş yap</h3>
                <p className="mt-1 text-sm text-[#c9c6f0]">Tarayıcıdan müşteri paneline gir, hesabını yönet ve izle.</p>
              </div>
            </div>
            <span className="relative inline-flex shrink-0 items-center gap-2 self-start md:self-auto rounded-full bg-white px-6 py-3 font-semibold text-[#0d0b3d] group-hover:bg-[#5b5fd6] group-hover:text-white transition-colors">
              Web Giriş Paneli <ArrowUpRight size={17} className="transition-transform group-hover:rotate-45" />
            </span>
          </a>
        </Reveal>
        <Reveal className="mt-10">
          <a href={wa("Merhaba, Dolunay TV kurulumu için yardım istiyorum.")} target="_blank" rel="noreferrer"
            data-testid="setup-help-btn" className="group inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 text-sm text-[#c9c6f0] hover:text-white hover:border-[#8a8dff]/60 transition-colors">
            <MessageCircle size={16} /> Kurulumda takıldın mı? WhatsApp'tan yaz, birlikte kuralım
          </a>
        </Reveal>
      </div>
    </section>
  );
}
